import { DatePicker, Form, Input, InputNumber, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanDisplayIndex } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { rangeToIso } from '@/shared/lib/dayRange'
import type { CsvColumn } from '@/shared/lib/download'
import { formatDateTime } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { OrgUnitTreeSelect } from '@/features/identity/OrgUnitTreeSelect'
import { useVoucherHolders } from '@/features/voucher/useVoucherHolders'
import {
  ACTIVATION_CHANNELS,
  ENTRY_METHODS,
  fetchVoucherActivationPage,
  type ActivationChannel,
  type VoucherActivation,
} from '@/features/voucher/voucherApi'

type Filters = TableState['filters']

const CHANNEL_COLOR: Readonly<Record<ActivationChannel, string>> = {
  STAFF: 'blue',
  PORTAL: 'green',
  PORTAL_ACCOUNT: 'cyan',
  SELFCARE: 'purple',
}

function single(values: readonly string[] | undefined): string | null {
  return values?.[0] ?? null
}

function known<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return allowed.find((item) => item === value) ?? null
}

export function VoucherActivationsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { state, patch } = useTableUrlState()
  const planNames = usePlanDisplayIndex(can('plan.read'))
  const holders = useVoucherHolders()

  const channel = known(single(state.filters['channel']), ACTIVATION_CHANNELS)
  const from = single(state.filters['from'])
  const to = single(state.filters['to'])
  const drawerCount = ['nodeId', 'planId', 'nas', 'batchNo', 'from']
    .filter((name) => single(state.filters[name]) !== null).length

  const commit = (next: Filters) => patch({ page: 1, filters: next })
  const dash = t('common.dash')
  const planLabel = (row: VoucherActivation) => planNames.get(row.planId) ?? shortId(row.planId)
  const channelLabel = (row: VoucherActivation) => {
    const value = known(row.channel, ACTIVATION_CHANNELS)
    return value ? t(`voucher.activations.channel.${value}`) : (row.channel ?? dash)
  }
  const entryLabel = (row: VoucherActivation) => {
    const value = known(row.entryMethod, ENTRY_METHODS)
    return value ? t(`voucher.activations.entryMethod.${value}`) : (row.entryMethod ?? dash)
  }
  const column = (key: string) => t(`voucher.activations.columns.${key}`)

  const columns: ColumnsType<VoucherActivation> = [
    {
      key: 'activatedAt',
      title: column('activatedAt'),
      width: 170,
      render: (_value, row) => formatDateTime(row.activatedAt),
    },
    {
      key: 'code',
      title: column('code'),
      width: 170,
      render: (_value, row) => <Typography.Text code copyable>{row.code}</Typography.Text>,
    },
    { key: 'batchNo', title: column('batchNo'), width: 100, align: 'right', dataIndex: 'batchNo' },
    { key: 'plan', title: column('plan'), width: 190, render: (_value, row) => planLabel(row) },
    {
      key: 'subscriber',
      title: column('subscriber'),
      width: 160,
      render: (_value, row) =>
        row.subscriberLogin ? (
          <Link to={`/subscribers?q=${encodeURIComponent(row.subscriberLogin)}`}>{row.subscriberLogin}</Link>
        ) : (
          dash
        ),
    },
    {
      key: 'channel',
      title: column('channel'),
      width: 170,
      render: (_value, row) => {
        const value = known(row.channel, ACTIVATION_CHANNELS)
        return value ? <Tag color={CHANNEL_COLOR[value]}>{channelLabel(row)}</Tag> : channelLabel(row)
      },
    },
    { key: 'entryMethod', title: column('entryMethod'), width: 150, render: (_value, row) => entryLabel(row) },
    { key: 'branch', title: column('branch'), width: 170, render: (_value, row) => row.branchName ?? dash },
    { key: 'nas', title: column('nas'), width: 160, render: (_value, row) => row.nasIdentifier ?? dash },
    { key: 'mac', title: column('mac'), width: 160, render: (_value, row) => row.clientMac ?? dash },
    { key: 'ip', title: column('ip'), width: 140, render: (_value, row) => row.clientIp ?? dash },
    {
      key: 'replaced',
      title: column('replaced'),
      width: 130,
      align: 'right',
      render: (_value, row) => row.replacedGrants ?? dash,
    },
    {
      key: 'activatedBy',
      title: column('activatedBy'),
      width: 170,
      render: (_value, row) => row.activatedByName ?? dash,
    },
  ]

  const exportColumns: readonly CsvColumn<VoucherActivation>[] = [
    { header: column('activatedAt'), value: (row) => formatDateTime(row.activatedAt) },
    { header: column('code'), value: (row) => row.code },
    { header: column('batchNo'), value: (row) => row.batchNo },
    { header: column('plan'), value: planLabel },
    { header: column('subscriber'), value: (row) => row.subscriberLogin },
    { header: column('channel'), value: channelLabel },
    { header: column('entryMethod'), value: entryLabel },
    { header: column('branch'), value: (row) => row.branchName },
    { header: column('nas'), value: (row) => row.nasIdentifier },
    { header: column('mac'), value: (row) => row.clientMac },
    { header: column('ip'), value: (row) => row.clientIp },
    { header: column('replaced'), value: (row) => row.replacedGrants },
    { header: column('activatedBy'), value: (row) => row.activatedByName },
  ]

  return (
    <>
      <PageHeader title={t('nav.voucherActivations')} description={t('pageIntro.voucherActivations')} />

      <DataTable<VoucherActivation>
        queryKey={queryKeys.vouchers.activations}
        fetchPage={fetchVoucherActivationPage}
        columns={columns}
        rowKey={(row) => row.voucherId}
        tableId="voucher-activations"
        stickyFirstColumn
        scrollX={2000}
        searchable
        searchPlaceholder={t('voucher.activations.searchPlaceholder')}
        exportColumns={exportColumns}
        exportFilename="voucher-activations"
        emptyTitle={t('voucher.activations.empty')}
        emptyDescription={t('voucher.activations.emptyHint')}
        toolbarExtra={
          <Space wrap>
            <Select<ActivationChannel | null>
              allowClear
              placeholder={column('channel')}
              value={channel}
              onChange={(value) => commit({ ...state.filters, channel: value ? [value] : [] })}
              style={{ width: 190 }}
              options={ACTIVATION_CHANNELS.map((value) => ({
                value,
                label: t(`voucher.activations.channel.${value}`),
              }))}
            />
            <FilterDrawer<Filters>
              value={state.filters}
              resetValue={{ ...state.filters, nodeId: [], planId: [], nas: [], batchNo: [], from: [], to: [] }}
              activeCount={drawerCount}
              onApply={commit}
            >
              {(draft, patchDraft) => (
                <>
                  <Form.Item label={t('voucher.activations.rangeLabel')}>
                    <DatePicker.RangePicker
                      allowClear
                      style={{ width: '100%' }}
                      value={
                        single(draft['from']) && single(draft['to'])
                          ? [dayjs(single(draft['from'])), dayjs(single(draft['to']))]
                          : null
                      }
                      onChange={(value) => {
                        const picked = value?.[0] && value[1] ? rangeToIso([value[0], value[1]]) : null
                        patchDraft({ from: picked ? [picked.from] : [], to: picked ? [picked.to] : [] })
                      }}
                    />
                  </Form.Item>

                  {can('org.node.read') ? (
                    <Form.Item label={column('branch')} extra={t('voucher.activations.branchHint')}>
                      <OrgUnitTreeSelect
                        units={holders.nodes}
                        types={holders.types}
                        value={single(draft['nodeId'])}
                        currentId={single(draft['nodeId'])}
                        onChange={(value) => patchDraft({ nodeId: value ? [value] : [] })}
                        loading={holders.loading}
                        allowClear
                      />
                    </Form.Item>
                  ) : null}

                  {can('plan.read') ? (
                    <Form.Item label={column('plan')}>
                      <Select<string | null>
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={single(draft['planId'])}
                        onChange={(value) => patchDraft({ planId: value ? [value] : [] })}
                        placeholder={t('voucher.activations.planPlaceholder')}
                        style={{ width: '100%' }}
                        options={[...planNames].map(([value, label]) => ({ value, label }))}
                      />
                    </Form.Item>
                  ) : null}

                  <Form.Item label={column('nas')}>
                    <Input
                      allowClear
                      value={single(draft['nas']) ?? ''}
                      onChange={(event) => patchDraft({ nas: event.target.value ? [event.target.value] : [] })}
                      placeholder={t('voucher.activations.nasPlaceholder')}
                    />
                  </Form.Item>

                  <Form.Item label={column('batchNo')}>
                    <InputNumber<number>
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                      value={single(draft['batchNo']) ? Number(single(draft['batchNo'])) : null}
                      onChange={(value) => patchDraft({ batchNo: value ? [String(value)] : [] })}
                      placeholder={t('voucher.activations.batchPlaceholder')}
                    />
                  </Form.Item>
                </>
              )}
            </FilterDrawer>
          </Space>
        }
      />

      {from && to ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('voucher.activations.rangeNote', { from: formatDateTime(from), to: formatDateTime(to) })}
        </Typography.Text>
      ) : null}
    </>
  )
}
